/**
 * Mapping a `sviw_WinClient_MyReports` record onto the report model, including
 * which output formats the report offers.
 */

import { parseRecordSource } from "./recordSource.js";
import type { Report, ReportFormat, ReportRecord } from "./types.js";

function isSet(value: boolean | number | null | undefined) {
	return value === true || value === 1;
}

/**
 * The formats a report may be rendered to, in the order the launcher offers
 * them.
 *
 * `FormatCSV` covers the whole spreadsheet family: the Windows launcher enables
 * "save as XLS" and "save as XLSX" from the same flag, because they all come out
 * of the same DevExpress export.
 */
export function getReportFormats(record: ReportRecord): ReportFormat[] {
	let formats: ReportFormat[] = [];

	if (isSet(record.FormatPDF)) formats.push("PDF");
	if (isSet(record.FormatCSV)) formats.push("XLSX", "XLS", "CSV");
	if (isSet(record.FormatHTML)) formats.push("HTML");
	if (isSet(record.FormatRTF)) formats.push("RTF");
	if (isSet(record.FormatMHT)) formats.push("MHT");
	if (isSet(record.FormatTEXT)) formats.push("TEXT");
	if (isSet(record.FormatIMAGE)) formats.push("IMAGE");

	return formats;
}

/** Maps a report record onto the model the launcher renders. */
export function toReport(record: ReportRecord): Report {
	let recordSource = parseRecordSource(record.RecordSource);

	return {
		reportId: record.ReportID ?? "",
		shortId: record.ShortID ?? null,
		title: record.Title?.trim() || null,
		description: record.Description?.trim() || null,
		viewName: recordSource?.viewName ?? null,
		initialFilter: recordSource?.filterString ?? null,
		hasTitle: isSet(record.HasTitle),
		hasHeader: isSet(record.HasHeader),
		formats: getReportFormats(record),
		timeout: record.Timeout ?? null,
	};
}
